import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ROUTES, buildTelehealthSessionPath, buildProviderSessionNotesPath } from "@/routes";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { 
  Video, 
  FileText, 
  Clock, 
  Users, 
  Phone, 
  Calendar,
  ChevronRight,
  Eye,
  Play,
  CheckCircle,
  AlertCircle,
  Search,
  UserCheck
} from "lucide-react";
import { ProviderSidebar } from "@/components/layout/ProviderSidebar";
import { ProviderHeader } from "@/components/layout/ProviderHeader";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { WaitingMembersList } from "@/components/telehealth/WaitingMembersList";
import { useAuditLog } from "@/hooks/useAuditLog";

// Mock upcoming sessions data
const mockSessions = [
  { id: "1", patientName: "John Smith", date: "2026-01-26", time: "10:00 AM", status: "scheduled", type: "Follow-up" },
  { id: "2", patientName: "Mary Johnson", date: "2026-01-26", time: "11:30 AM", status: "waiting", type: "New Patient" },
  { id: "3", patientName: "Robert Davis", date: "2026-01-26", time: "2:00 PM", status: "scheduled", type: "Check-up" },
  { id: "4", patientName: "Sarah Wilson", date: "2026-01-27", time: "9:00 AM", status: "scheduled", type: "Follow-up" },
];

const mockCompletedSessions = [
  { id: "5", patientName: "James Brown", date: "2026-01-25", duration: 32, billingUnits: 3, status: "documented" },
  { id: "6", patientName: "Emily Clark", date: "2026-01-25", duration: 18, billingUnits: 2, status: "documented" },
  { id: "7", patientName: "Michael Lee", date: "2026-01-24", duration: 45, billingUnits: 3, status: "pending_notes" },
];

export default function ProviderTelehealth() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIntake, setSelectedIntake] = useState<any>(null);
  const { logIntakeFormView } = useAuditLog();

  // Log audit event when viewing an intake form
  useEffect(() => {
    if (selectedIntake) {
      logIntakeFormView(
        selectedIntake.id,
        selectedIntake.user_id,
        `${selectedIntake.first_name} ${selectedIntake.last_name}`
      );
    }
  }, [selectedIntake, logIntakeFormView]);

  // Fetch intake forms
  const { data: intakeForms, isLoading: intakeLoading } = useQuery({
    queryKey: ["intake-forms"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("patients")
        .select("*")
        .order("created_at", { ascending: false });
      
      if (error) throw error;
      return data;
    },
  });

  const filteredIntakes = intakeForms?.filter((form) =>
    `${form.first_name} ${form.last_name}`.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "waiting":
        return <Badge variant="default" className="bg-green-500 hover:bg-green-600">Waiting</Badge>;
      case "scheduled":
        return <Badge variant="secondary">Scheduled</Badge>;
      case "documented":
        return <Badge variant="outline" className="border-green-500 text-green-600">Documented</Badge>;
      case "pending_notes":
        return <Badge variant="outline" className="border-yellow-500 text-yellow-600">Needs Notes</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getIntakeStatusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return <Badge variant="outline" className="border-yellow-500 text-yellow-600">Pending Review</Badge>;
      case "approved":
        return <Badge variant="outline" className="border-green-500 text-green-600">Approved</Badge>;
      case "rejected":
        return <Badge variant="destructive">Rejected</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <ProviderSidebar />
      <div className="ml-64">
        <ProviderHeader />
        <main className="p-6">
          {/* Page Header */}
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="font-display text-3xl font-bold text-foreground">Telehealth Center</h1>
              <p className="mt-1 text-muted-foreground">
                Manage video sessions, view patient intakes, and document visits
              </p>
            </div>
            <Button onClick={() => navigate(ROUTES.TELEHEALTH_SESSION)} className="gap-2">
              <Video className="h-4 w-4" />
              Start New Session
            </Button>
          </div>

          {/* Quick Stats */}
          <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-4">
            <Card>
              <CardContent className="flex items-center gap-4 p-4">
                <div className="rounded-full bg-primary/10 p-3">
                  <Calendar className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold">4</p>
                  <p className="text-sm text-muted-foreground">Today's Sessions</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="flex items-center gap-4 p-4">
                <div className="rounded-full bg-green-500/10 p-3">
                  <Users className="h-5 w-5 text-green-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">1</p>
                  <p className="text-sm text-muted-foreground">In Waiting Room</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="flex items-center gap-4 p-4">
                <div className="rounded-full bg-yellow-500/10 p-3">
                  <FileText className="h-5 w-5 text-yellow-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{intakeForms?.filter(f => f.status === "pending").length || 0}</p>
                  <p className="text-sm text-muted-foreground">Pending Intakes</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="flex items-center gap-4 p-4">
                <div className="rounded-full bg-orange-500/10 p-3">
                  <AlertCircle className="h-5 w-5 text-orange-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">1</p>
                  <p className="text-sm text-muted-foreground">Needs Documentation</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Main Tabs */}
          <Tabs defaultValue="sessions" className="space-y-6">
            <TabsList>
              <TabsTrigger value="sessions" className="gap-2">
                <Video className="h-4 w-4" />
                Sessions
              </TabsTrigger>
              <TabsTrigger value="intakes" className="gap-2">
                <FileText className="h-4 w-4" />
                Patient Intakes
              </TabsTrigger>
              <TabsTrigger value="history" className="gap-2">
                <Clock className="h-4 w-4" />
                Session History
              </TabsTrigger>
            </TabsList>

            {/* Sessions Tab */}
            <TabsContent value="sessions" className="space-y-6">
              {/* Waiting Members Section */}
              <WaitingMembersList />

              <Card>
                <CardHeader>
                  <CardTitle>Upcoming Sessions</CardTitle>
                  <CardDescription>Your scheduled telehealth appointments</CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Patient</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Time</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {mockSessions.map((session) => (
                        <TableRow key={session.id}>
                          <TableCell className="font-medium">{session.patientName}</TableCell>
                          <TableCell>{session.date}</TableCell>
                          <TableCell>{session.time}</TableCell>
                          <TableCell>{session.type}</TableCell>
                          <TableCell>{getStatusBadge(session.status)}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              {session.status === "waiting" ? (
                                <Button 
                                  size="sm" 
                                  onClick={() => navigate(buildTelehealthSessionPath(session.id))}
                                  className="gap-1"
                                >
                                  <Phone className="h-4 w-4" />
                                  Join Now
                                </Button>
                              ) : (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => navigate(buildTelehealthSessionPath(session.id))}
                                  className="gap-1"
                                >
                                  <Play className="h-4 w-4" />
                                  Start
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Patient Intakes Tab */}
            <TabsContent value="intakes" className="space-y-6">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Patient Intake Forms</CardTitle>
                      <CardDescription>Review submitted patient information</CardDescription>
                    </div>
                    <div className="relative w-64">
                      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        placeholder="Search patients..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-9"
                      />
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {intakeLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                    </div>
                  ) : filteredIntakes?.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      No intake forms found
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Patient Name</TableHead>
                          <TableHead>DOB</TableHead>
                          <TableHead>Insurance</TableHead>
                          <TableHead>Submitted</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredIntakes?.map((form) => (
                          <TableRow key={form.id}>
                            <TableCell className="font-medium">
                              {form.first_name} {form.last_name}
                            </TableCell>
                            <TableCell>{format(new Date(form.date_of_birth), "MM/dd/yyyy")}</TableCell>
                            <TableCell className="capitalize">
                              {form.insurance_type.replace("_", " ")}
                            </TableCell>
                            <TableCell>{format(new Date(form.created_at), "MM/dd/yyyy")}</TableCell>
                            <TableCell>{getIntakeStatusBadge(form.status || "pending")}</TableCell>
                            <TableCell className="text-right">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => setSelectedIntake(form)}
                                className="gap-1"
                              >
                                <Eye className="h-4 w-4" />
                                View
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Session History Tab */}
            <TabsContent value="history" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Completed Sessions</CardTitle>
                  <CardDescription>Past telehealth sessions and documentation status</CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Patient</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Duration</TableHead>
                        <TableHead>Billing Units</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {mockCompletedSessions.map((session) => (
                        <TableRow key={session.id}>
                          <TableCell className="font-medium">{session.patientName}</TableCell>
                          <TableCell>{session.date}</TableCell>
                          <TableCell>{session.duration} min</TableCell>
                          <TableCell>
                            <Badge variant="secondary">{session.billingUnits} units</Badge>
                          </TableCell>
                          <TableCell>{getStatusBadge(session.status)}</TableCell>
                          <TableCell className="text-right">
                            <Button 
                              size="sm" 
                              variant={session.status === "pending_notes" ? "default" : "outline"}
                              className="gap-1"
                              onClick={() => navigate(buildProviderSessionNotesPath(session.id))}
                            >
                              <FileText className="h-4 w-4" />
                              {session.status === "pending_notes" ? "Add Notes" : "View Notes"}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </main>
      </div>

      {/* Intake Form Detail Dialog */}
      <Dialog open={!!selectedIntake} onOpenChange={() => setSelectedIntake(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle>
              Patient Intake: {selectedIntake?.first_name} {selectedIntake?.last_name}
            </DialogTitle>
            <DialogDescription>
              Submitted on {selectedIntake && format(new Date(selectedIntake.created_at), "MMMM d, yyyy")}
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh] pr-4">
            {selectedIntake && (
              <div className="space-y-6">
                {/* Personal Information */}
                <div>
                  <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                    <Users className="h-4 w-4" /> Personal Information
                  </h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Name:</span>
                      <p className="font-medium">{selectedIntake.first_name} {selectedIntake.last_name}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Date of Birth:</span>
                      <p className="font-medium">{format(new Date(selectedIntake.date_of_birth), "MM/dd/yyyy")}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Phone:</span>
                      <p className="font-medium">{selectedIntake.phone}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Email:</span>
                      <p className="font-medium">{selectedIntake.email || "N/A"}</p>
                    </div>
                    <div className="col-span-2">
                      <span className="text-muted-foreground">Address:</span>
                      <p className="font-medium">
                        {selectedIntake.address_line1}
                        {selectedIntake.address_line2 && `, ${selectedIntake.address_line2}`}
                        <br />
                        {selectedIntake.city}, {selectedIntake.state} {selectedIntake.zip_code}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Insurance Information */}
                <div>
                  <h4 className="font-semibold text-foreground mb-3">Insurance Information</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Type:</span>
                      <p className="font-medium capitalize">{selectedIntake.insurance_type.replace("_", " ")}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Medicare ID:</span>
                      <p className="font-medium">{selectedIntake.medicare_id || "N/A"}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Medicaid ID:</span>
                      <p className="font-medium">{selectedIntake.medicaid_id || "N/A"}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Coverage:</span>
                      <div className="flex gap-1 flex-wrap mt-1">
                        {selectedIntake.medicare_part_a && <Badge variant="outline">Part A</Badge>}
                        {selectedIntake.medicare_part_b && <Badge variant="outline">Part B</Badge>}
                        {selectedIntake.medicare_part_c && <Badge variant="outline">Part C</Badge>}
                        {selectedIntake.medicare_part_d && <Badge variant="outline">Part D</Badge>}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Medical Information */}
                <div>
                  <h4 className="font-semibold text-foreground mb-3">Medical Information</h4>
                  <div className="space-y-3 text-sm">
                    <div>
                      <span className="text-muted-foreground">Primary Care Physician:</span>
                      <p className="font-medium">{selectedIntake.primary_care_physician || "N/A"}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Current Medications:</span>
                      <p className="font-medium">{selectedIntake.current_medications || "None listed"}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Allergies:</span>
                      <p className="font-medium">{selectedIntake.allergies || "None listed"}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Chronic Conditions:</span>
                      <p className="font-medium">{selectedIntake.chronic_conditions || "None listed"}</p>
                    </div>
                  </div>
                </div>

                {/* Emergency Contact */}
                <div>
                  <h4 className="font-semibold text-foreground mb-3">Emergency Contact</h4>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Name:</span>
                      <p className="font-medium">{selectedIntake.emergency_contact_name}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Phone:</span>
                      <p className="font-medium">{selectedIntake.emergency_contact_phone}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Relationship:</span>
                      <p className="font-medium">{selectedIntake.emergency_contact_relationship}</p>
                    </div>
                  </div>
                </div>

                {/* Social & Lifestyle */}
                {(selectedIntake.living_situation || selectedIntake.hobbies?.length > 0) && (
                  <div>
                    <h4 className="font-semibold text-foreground mb-3">Social & Lifestyle</h4>
                    <div className="space-y-3 text-sm">
                      {selectedIntake.living_situation && (
                        <div>
                          <span className="text-muted-foreground">Living Situation:</span>
                          <p className="font-medium capitalize">{selectedIntake.living_situation.replace("_", " ")}</p>
                        </div>
                      )}
                      {selectedIntake.social_support_level && (
                        <div>
                          <span className="text-muted-foreground">Support Level:</span>
                          <p className="font-medium capitalize">{selectedIntake.social_support_level.replace("_", " ")}</p>
                        </div>
                      )}
                      {selectedIntake.hobbies?.length > 0 && (
                        <div>
                          <span className="text-muted-foreground">Hobbies:</span>
                          <div className="flex gap-1 flex-wrap mt-1">
                            {selectedIntake.hobbies.map((hobby: string) => (
                              <Badge key={hobby} variant="secondary" className="capitalize">
                                {hobby.replace("_", " ")}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </ScrollArea>
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setSelectedIntake(null)}>
              Close
            </Button>
            <Button onClick={() => navigate(ROUTES.TELEHEALTH_SESSION)} className="gap-2">
              <Video className="h-4 w-4" />
              Start Session
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
